import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Flex, HorizontalLine } from "@faire/web/slate/Layout";
import { GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IDepartment } from "@faire/retailer-shared/greenhouse/greenhouseApi";
import React from "react";
import styled from "styled-components";

import {
  DESIGN_DEPARTMENT,
  EyebrowText,
  SectionHeadline,
} from "@faire/retailer/views/careers/__internal__/common/common";
import { JobOpeningJobsWithTier } from "@faire/retailer/views/careers/__internal__/JobOpenings/JobOpeningJobsWithTier";

const JobOpeningsDivider: React.FC = () => {
  return (
    <>
      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />
      <HorizontalLine />
      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />
    </>
  );
};

const JobOpeningsDepartment: React.FC<{
  department: IDepartment;
  isFirst: boolean;
}> = ({ department, isFirst }) => {
  const viewport = useViewport();
  const spacing = useResponsiveValue({
    mobileAndAbove: <Spacer height="3x" />,
    tabletAndAbove: <Spacer height="5x" />,
    desktopAndAbove: <GrowingSpacer />,
  });

  const getOpenRoles = () => {
    const childDepartments = department.childDepartments;
    if (childDepartments.length > 0) {
      return childDepartments
        .map((department: IDepartment) => department.jobs.length)
        .reduce(
          (accumulatedQuantity, currentSubDepartmentJobQuantity) =>
            accumulatedQuantity + currentSubDepartmentJobQuantity
        );
    }
    return department.jobs.length;
  };

  return (
    <>
      {isFirst ? null : <JobOpeningsDivider />}
      <Typography variant="displaySSerifRegular">{department.title}</Typography>
      <Spacer height={viewport.isMobile ? "1x" : "3x"} />
      <ResponsiveContainer>
        {department.name === DESIGN_DEPARTMENT ? (
          <Column>
            <Spacer height="1x" />
            <Button
              linkTo="https://www.design.faire.com/"
              target="_blank"
              variant="tertiary"
            >
              <StrictLocalMsg
                defaultMessage="Discover Design at Faire"
                description={{
                  text: "Link to a page with specific information about how's design at Faire",
                }}
              />
              <Spacer width="1x" />
              <Arrow direction="right" />
            </Button>
          </Column>
        ) : null}
        {spacing}
        <List>
          <EyebrowText>
            <StrictLocalMsg
              defaultMessage="{openRolesNumber, plural, one{# open role} other{# open roles}}"
              description={{
                text: "Number of open roles for a given department in a job openings page",
              }}
              values={{
                openRolesNumber: getOpenRoles(),
              }}
            />
          </EyebrowText>
          <Spacer height="2x" />
          <JobOpeningJobsWithTier department={department} />
        </List>
      </ResponsiveContainer>
    </>
  );
};

export const JobOpeningsList = ({
  departments,
}: {
  departments: IDepartment[];
}) => {
  if (departments.length === 0) {
    return (
      <Column align="center">
        <Spacer height="5x" />
        <SectionHeadline>
          <StrictLocalMsg
            defaultMessage="No current openings"
            description={{
              text: "text to tell the user that there are no current openings",
            }}
          />
        </SectionHeadline>
        <Spacer height="5x" />
      </Column>
    );
  }
  return (
    <Column>
      {departments.map((department, i) => (
        <JobOpeningsDepartment
          department={department}
          isFirst={i === 0}
          key={department.name}
        />
      ))}
    </Column>
  );
};

const ResponsiveContainer = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", desktopAndAbove: "row" },
})``;

const List = styled(Column)`
  width: 100%;
  max-width: 540px;

  ${responsiveMediaQueries.tablet} {
    align-self: center;
  }
`;
